import axios from 'axios'

// `cn` vit désormais dans @gozoki/ui (source unique, partagée avec store-app).
// Les formateurs métier ci-dessous restent locaux au back-office.
export { cn } from '@gozoki/ui/lib/utils'

const frenchPluralRules = new Intl.PluralRules('fr')

/**
 * Suffixe pluriel français (`''` ou `'s'`) selon `count`, via `Intl.PluralRules`.
 * En français, 0 et 1 sont singuliers (« 0 permission manquante »).
 * Ex. : `Permission${frenchPluralSuffix(n)} manquante${frenchPluralSuffix(n)}`.
 */
export function frenchPluralSuffix(count: number): '' | 's' {
  return frenchPluralRules.select(count) === 'one' ? '' : 's'
}

export function formatPercentage(
  percentage: number,
  options?: { withSign?: boolean; withPercent?: boolean },
) {
  const { withSign = true, withPercent = true } = options ?? {}

  let result = ''
  if (Number.isNaN(percentage) || percentage === Number.NEGATIVE_INFINITY) {
    result = '0'
  } else if (percentage === Number.POSITIVE_INFINITY) {
    result = '100'
  } else {
    result = percentage < 10 ? percentage.toFixed(1) : formatNumber(percentage)
  }

  result = `${result.replace('.', ',')}${withPercent ? '%' : ''}`

  if (withSign) {
    result = `${percentage > 0 ? '+' : ''}${result}`
  }

  return result
}

export function getAxiosErrorMessage(error: unknown, fallbackMessage?: string) {
  if (
    axios.isAxiosError(error) &&
    error.response?.data &&
    'message' in error.response.data &&
    typeof error.response.data.message === 'string'
  ) {
    return error.response.data.message
  }

  if (
    axios.isAxiosError(error) &&
    error.response?.data &&
    'error' in error.response.data &&
    typeof error.response.data.error === 'string'
  ) {
    return error.response.data.error
  }

  return fallbackMessage ?? 'Une erreur inconnue est survenue, veuillez contacter le support'
}

export function getTuyauErrorMessage(error: unknown, fallbackMessage?: string) {
  if (error && typeof error === 'object' && 'response' in error) {
    const body = (error as { response?: unknown }).response
    if (body && typeof body === 'object') {
      if ('error' in body && typeof body.error === 'string') return body.error
      if ('message' in body && typeof body.message === 'string') return body.message
      if (
        'errors' in body &&
        Array.isArray(body.errors) &&
        typeof body.errors[0]?.message === 'string'
      ) {
        return body.errors[0].message
      }
    }
  }

  if (error instanceof Error && error.message) return error.message

  return fallbackMessage ?? 'Une erreur inconnue est survenue, veuillez contacter le support'
}

/** Statut HTTP d'une erreur Tuyau (`TuyauHTTPError.status`), `null` pour toute autre erreur. */
export function getTuyauErrorStatus(error: unknown): number | null {
  if (!error || typeof error !== 'object' || !('status' in error)) return null
  const { status } = error as { status?: unknown }
  return typeof status === 'number' ? status : null
}

/** Délai (secondes, > 0) d'un 429 qui porte `{ retryAfter }` dans son corps ; `null` sinon. */
export function getTuyauRetryAfterSeconds(error: unknown): number | null {
  if (getTuyauErrorStatus(error) !== 429) return null
  const body =
    error && typeof error === 'object' && 'response' in error
      ? (error as { response?: { retryAfter?: unknown } }).response
      : undefined
  const retryAfter = body?.retryAfter
  return typeof retryAfter === 'number' && retryAfter > 0 ? retryAfter : null
}

export interface ApiFieldError<Field extends string> {
  field: Field
  message: string
}

/** `errors` d'un corps 422 : `error.response` pour Tuyau, `error.response.data` pour axios (legacy). */
const validationErrorsOf = (error: unknown): unknown[] | null => {
  if (!error || typeof error !== 'object' || !('response' in error)) return null
  const { response } = error as { response?: { errors?: unknown; data?: { errors?: unknown } } }
  if (!response || typeof response !== 'object') return null
  if (Array.isArray(response.errors)) return response.errors
  if (Array.isArray(response.data?.errors)) return response.data.errors
  return null
}

/**
 * Erreurs de champ d'une réponse 422 VineJS (`{ errors: [{ field, message }] }`), d'une erreur
 * Tuyau comme d'une erreur axios (`@gozoki/api`, legacy), rapportées aux champs d'un formulaire :
 * `fields` est soit la table champ de l'API → champ du formulaire, soit la liste des champs du
 * formulaire qui portent le même nom côté API. Un champ inconnu est ignoré (il part en toast chez
 * l'appelant) ; `channels.0` (élément d'un tableau) se lit `channels`.
 */
export function getApiFieldErrors<Field extends string>(
  error: unknown,
  fields: Readonly<Record<string, Field>> | readonly Field[],
): ApiFieldError<Field>[] {
  const errors = validationErrorsOf(error)
  if (errors === null) return []
  const formFieldOf = (apiField: string): Field | undefined => {
    if (!Array.isArray(fields)) return (fields as Readonly<Record<string, Field>>)[apiField]
    return (fields as readonly string[]).includes(apiField) ? (apiField as Field) : undefined
  }
  const result: ApiFieldError<Field>[] = []
  for (const entry of errors) {
    if (!entry || typeof entry !== 'object') continue
    const { field: apiField, message } = entry as { field?: unknown; message?: unknown }
    if (typeof apiField !== 'string' || typeof message !== 'string') continue
    const field = formFieldOf(apiField.split('.')[0]!)
    if (field !== undefined) result.push({ field, message })
  }
  return result
}

export function trimSeconds<T extends string | null>(time: T): T {
  if (!time) return time
  return time.slice(0, 5) as T
}

export const numberFormatter = new Intl.NumberFormat('fr-FR')
export const euroFormatter = new Intl.NumberFormat('fr-FR', {
  style: 'currency',
  currency: 'EUR',
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
})

export const formatNumber = (value: number) => numberFormatter.format(Math.round(value))

export const formatCentsToEuro = (value: number) => euroFormatter.format(Math.round(value / 100))

export const formatNumberKEuros = (value: number) =>
  `${numberFormatter.format(Math.round(value / 1000))}k €`
